/** @file bone.split's meshes: its regions become grid meshes and every weight on it is shared across the cut. */
import type { Draft } from 'immer';
import { affine } from '@pose2d/math/affine';
import { slugifyName } from '@pose2d/format/slugifyName';
import type { CommandCall, Doc, Id, MeshAtt, RegionAtt } from '@pose2d/format/types';
import type { CommandContext } from '../commandContext';
import { applyNested } from '../autorig/applyNested';
import { meshSlotBones } from '../mesh/meshSlotBones';
import { setupWorldOf } from '../mesh/setupWorldOf';
import { skinBlend } from '../mesh/skinBlend';
import { splitBand } from './splitBand';
import { splitGrid } from './splitGrid';
import { splitRamp } from './splitRamp';

/** The cut: the split bone (it keeps [0, cut)), the new bone, and the bone's length before the split. */
export type Cut = { bone: Id; added: Id; cut: number; length: number };

/** One nested command on the draft (its errors and warnings under `target`); returns what it created. */
function run(draft: Draft<Doc>, ctx: CommandContext, call: CommandCall): Record<string, Id> {
  const made = applyNested(draft, call, ctx, 'target');
  Object.assign(ctx.created, made);
  return made;
}

/** Attachments the skins show in slots on `bone`, each once, in skin then entry order. */
function shownOn(doc: Doc, bone: Id): Id[] {
  return [...new Set(Object.values(doc.skins).flatMap((s) => s.entries.filter((e) => doc.slots[e.slot]?.bone === bone).map((e) => e.attachment)))];
}

/**
 * Drops the stale-bind INFO ("call mesh.rebindMesh first") a nested mesh.setWeights added from `from` on: after a split
 * bindAddedAtCut gives the new bone the split bone's bind, so no vertex moves at setup and the advice would be false.
 */
function dropStaleBindInfo(ctx: CommandContext, from: number): void {
  for (let i = ctx.warnings.length - 1; i >= from; i--) {
    const w = ctx.warnings[i];
    if (w?.code === 'INFO' && w.message.includes('mesh.rebindMesh')) ctx.warnings.splice(i, 1);
  }
}

/** Each vertex in the split bone's setup space: rigid vertices as stored (slot bone = the split bone), skinned ones blended. */
function localPoints(doc: Doc, mesh: MeshAtt, id: Id, bone: Id): number[] {
  const g = mesh.geometry;
  if (g.kind === 'rigid') return [...g.vertices];
  const worldOf = setupWorldOf(doc);
  const mats = skinBlend(g, worldOf, `attachments.${id}.geometry`);
  const inverse = affine.invert(new Float64Array(6), worldOf(bone));
  return g.weights.flatMap((_, v) => {
    const at = affine.apply(mats.subarray(6 * v, 6 * v + 6), g.bindVertices[2 * v] ?? 0, g.bindVertices[2 * v + 1] ?? 0, [0, 0]);
    return affine.apply(inverse, at[0], at[1], [0, 0]);
  });
}

/** Influences mesh.setWeights keeps per vertex (normalizeVertexWeights drops the lightest beyond it). */
const MAX_INFLUENCES = 4;

/**
 * mesh.setWeights rows: every influence on the split bone shared by the ramp (a just-bound mesh is fully on it). A vertex
 * the share takes past MAX_INFLUENCES loses its lightest influence in mesh.setWeights; one INFO names the count.
 */
function weightsOf(ctx: CommandContext, mesh: MeshAtt, ramp: readonly number[], cut: Cut): [string, number][][] {
  const rows = mesh.geometry.kind === 'skinned' ? mesh.geometry.weights : ramp.map((): [Id, number][] => [[cut.bone, 1]]);
  const out = rows.map((row, v) => {
    const r = ramp[v] ?? 0;
    return row.flatMap(([b, w]): [string, number][] => (b === cut.bone ? [[cut.bone, w * (1 - r)], [cut.added, w * r]] : [[b, w]]));
  });
  const over = out.filter((row) => row.filter(([, w]) => w > 0).length > MAX_INFLUENCES).length;
  if (over > 0) ctx.warnings.push({ code: 'INFO', message: `${mesh.name}: ${over} vertices already had ${MAX_INFLUENCES} influences; each lost its lightest one to the new bone's share`, path: 'target' });
  return out;
}

/** A copy of the mesh's bind of `bone`; undefined unless the mesh is skinned and weighted to it. */
function bindOf(mesh: MeshAtt, bone: Id): number[] | undefined {
  return mesh.geometry.kind === 'skinned' ? mesh.geometry.bindPose[bone]?.slice() : undefined;
}

/**
 * On a mesh that was already skinned, the new bone binds at the split bone's bind followed by the cut, B_added =
 * B_bone·T(cut), not at its setup world: W_added·B_added⁻¹ = W_bone·B_bone⁻¹ while the new bone keeps its setup local,
 * so the shares stay one rigid transform and the part does not move even when B_bone ≠ W_bone (mesh.rebindMesh, which
 * re-captures every bind at setup, sees the same per-vertex skin either way). `bind` is B_bone read before the share:
 * when the share takes the bone off every vertex, mesh.setWeights drops its bindPose entry.
 */
function bindAddedAtCut(mesh: MeshAtt, cut: Cut, bind: readonly number[]): void {
  const g = mesh.geometry;
  if (g.kind !== 'skinned' || !Object.hasOwn(g.bindPose, cut.added)) return;
  g.bindPose[cut.added] = Array.from(affine.mul(new Float64Array(6), Float64Array.from(bind), Float64Array.from([1, 0, 0, 1, cut.cut, 0])));
}

/**
 * A region on the bone becomes `<region>-mesh` (splitGrid, its band from the region's corners), every entry of a slot
 * on the bone moves to it, and the region is deleted unless slots on other bones still show it (INFO).
 */
function meshRegion(draft: Draft<Doc>, ctx: CommandContext, id: Id, region: RegionAtt, cut: Cut): Id {
  const place = affine.fromLocal([0, 0, 0, 0, 0, 0], region.x, region.y, region.rotation, region.scaleX, region.scaleY, 0, 0);
  const corners = [-1, 1].flatMap((sx) => [-1, 1].flatMap((sy) => affine.apply(place, (sx * region.width) / 2, (sy * region.height) / 2, [0, 0])));
  const name = slugifyName(`${region.name}-mesh`, new Set(Object.values(draft.attachments).map((a) => a.name)), 0).name;
  const geometry = splitGrid(region, cut.cut, splitBand(corners, cut.cut, cut.length));
  const mesh = run(draft, ctx, { name: 'attachment.createMesh', input: { name, image: region.image, color: region.color, ...geometry } })[name] ?? '';
  let elsewhere = false;
  for (const skin of Object.values(draft.skins)) {
    for (const e of skin.entries.filter((x) => x.attachment === id)) {
      if (draft.slots[e.slot]?.bone !== cut.bone) elsewhere = true;
      else run(draft, ctx, { name: 'skin.setEntry', input: { skin: skin.name, slot: e.slot, placeholder: e.placeholder, attachment: mesh } });
    }
  }
  if (!elsewhere) run(draft, ctx, { name: 'attachment.delete', input: { target: id } });
  else ctx.warnings.push({ code: 'INFO', message: `region ${region.name} is kept: slots on other bones still show it`, path: 'target' });
  return mesh;
}

/**
 * The mesh to weight for attachment `id`: a region becomes one (meshRegion); mounted or handle-bearing items, and rigid
 * meshes that slots on other bones also show (skinning them to the split bone would move those slots' art), are left (INFO).
 */
function meshFor(draft: Draft<Doc>, ctx: CommandContext, id: Id, cut: Cut): Id | null {
  const att = draft.attachments[id];
  if (!att) return null;
  if (att.mount || (att.type === 'region' && att.handle)) {
    ctx.warnings.push({ code: 'INFO', message: `${att.name} is an item (mounted or with a handle); it stays rigid`, path: 'target' });
    return null;
  }
  if (att.type === 'mesh' && att.geometry.kind === 'rigid' && [...meshSlotBones(draft, id)].some((b) => b !== cut.bone)) {
    ctx.warnings.push({ code: 'INFO', message: `${att.name} is also shown in slots on other bones; it stays rigid`, path: 'target' });
    return null;
  }
  return att.type === 'region' ? meshRegion(draft, ctx, id, att, cut) : id;
}

/** Binds mesh `target` to the bone if rigid, shares the bone's weight with the new bone, and keeps a skinned one still. */
function weighMesh(draft: Draft<Doc>, ctx: CommandContext, target: Id, cut: Cut): void {
  const mesh = draft.attachments[target];
  if (mesh?.type !== 'mesh') return;
  const oldBind = bindOf(mesh, cut.bone);
  if (mesh.geometry.kind !== 'skinned') run(draft, ctx, { name: 'mesh.setBinding', input: { target, binding: 'skinned', bone: cut.bone } });
  const bound = draft.attachments[target];
  if (bound?.type !== 'mesh') return;
  const points = localPoints(draft, bound, target, cut.bone);
  const ramp = splitRamp(points, cut.cut, splitBand(points, cut.cut, cut.length));
  const from = ctx.warnings.length;
  run(draft, ctx, { name: 'mesh.setWeights', input: { target, weights: weightsOf(ctx, bound, ramp, cut) } });
  dropStaleBindInfo(ctx, from);
  const weighted = draft.attachments[target];
  if (oldBind !== undefined && weighted?.type === 'mesh') bindAddedAtCut(weighted, cut, oldBind);
}

/**
 * bone.split's mesh pass (AX1-08): every unmounted region a slot on the bone shows becomes a grid mesh, every rigid mesh
 * only slots on the bone show is bound to the bone (mesh.setBinding; one also shown on another bone stays rigid, INFO), and every skinned mesh weighted to the bone (on any slot)
 * shares that weight with the new bone by splitRamp over its own band (mesh.setWeights; normalized, W2-29).
 */
export function splitMeshes(draft: Draft<Doc>, ctx: CommandContext, cut: Cut): void {
  const skinned = Object.entries(draft.attachments).flatMap(([id, a]) => (a.type === 'mesh' && a.geometry.kind === 'skinned' && Object.hasOwn(a.geometry.bindPose, cut.bone) ? [id] : []));
  for (const id of new Set([...shownOn(draft, cut.bone), ...skinned])) {
    const target = meshFor(draft, ctx, id, cut);
    if (target !== null) weighMesh(draft, ctx, target, cut);
  }
}
